import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import {
  Label,
  Avatar,
  Icon,
  Autocomplete,
  AutocompleteItem,
} from 'mw-style-react';
import cn from 'classnames';
import {
  SEARCH_LAYERS,
  GET_LAYERS,
} from '@control-front-end/common/constants/graphLayers';
import useScroll from 'useScroll';
import useOutsideClick from 'useOutsideClick';
import useIntl from 'useIntl';
import mes from './intl';
import './SelectLayer.scss';

/**
 * Поиск шаблонов акторов
 */
function SelectLayer(props) {
  const {
    id,
    value,
    error,
    placeholder,
    helperText,
    isDisabled,
    onChange,
    onBlur,
    size = 'medium',
    type = 'default',
    label,
  } = props;
  const t = useIntl();
  const dispatch = useDispatch();
  const searchBox = useRef();
  const [query, setQuery] = useState('');
  const [focus, setFocus] = useState(false);
  const initState = {
    list: [],
    limit: 15,
    offset: 0,
    endList: false,
    reqStatus: 'success',
  };
  const [layers, setLayers] = useState(initState);
  const [search, setSearch] = useState([]);

  const clearSearch = () => {
    setQuery('');
    setFocus(false);
  };

  const getData = (loadMore = false) => {
    dispatch({
      type: GET_LAYERS.REQUEST,
      payload: { loadMore, layersState: layers },
      callback: (data) => setLayers({ ...layers, ...data }),
    });
  };

  useEffect(() => {
    getData();
  }, []);
  useOutsideClick({ ref: searchBox, callback: clearSearch });
  useScroll({ ref: searchBox, callback: () => getData(true) });

  /**
   * Обработка поискового запроса
   * @param q
   */
  const handleChange = ({ value: q }) => {
    setQuery(q);
    if (!q.length) {
      setSearch([]);
      return;
    }
    if (q.length < 2) return;

    dispatch({
      type: SEARCH_LAYERS.REQUEST,
      payload: { query: q },
      callback: (data) => setSearch(data),
    });
  };

  /**
   * Обработка клика по найденному варианту
   * @param item
   */
  const handleItemClick = (item) => {
    const newItem = { id: item.id, name: item.name };
    onChange({ id, value: newItem });
    setFocus(false);
    setQuery('');
    setSearch([]);
  };

  /**
   * Обработка снятия фокуса
   */
  const handleBlur = () => {
    setFocus(false);
    setQuery('');
    if (!onBlur) return;
    onBlur({ id, value: query });
  };

  const emptyResult = () => (
    <AutocompleteItem
      id="not_found"
      size="small"
      value="Not found"
      onClick={handleBlur}
    />
  );

  const renderSearchResult = () => {
    if (!focus) return;
    const isSearch = query.length > 2;
    const list = isSearch ? search : layers.list;
    const resultList = list.filter(
      (layer) =>
        !layer.isSystem ||
        layer.name === 'external' ||
        layer.name === 'internal'
    );
    if (!isSearch && !resultList.length) return;

    const items = resultList.map((i) => (
      <AutocompleteItem
        styleName="sl__item"
        key={i.id}
        id={i.id}
        value={i.name}
        onClick={() => handleItemClick(i)}
        children={
          <div styleName="sl__item__avatar__box">
            <Avatar styleName="sl__item__avatar">
              <Icon type="layer" size="small" />
            </Avatar>
          </div>
        }
      />
    ));
    return <div>{items.length ? items : emptyResult()}</div>;
  };

  const renderSearchTitle = () => {
    const resultLabel = query.length > 2 ? t(mes.searchResults) : t(mes.recent);
    return (
      <Label styleName="sl__title" fontWeight="semibold" value={resultLabel} />
    );
  };

  return (
    <div ref={searchBox}>
      <Autocomplete
        styleName={cn('sl__field', type)}
        size={size}
        placeholder={placeholder}
        label={label}
        value={focus ? query : value.name || '---'}
        bordered={true}
        error={error}
        helperText={helperText}
        onChange={handleChange}
        onBlur={handleBlur}
        onFocus={() => setFocus(true)}
        visibility={isDisabled ? 'disabled' : 'visible'}
        searchResult={renderSearchResult}
        searchTitle={renderSearchTitle}
        chipsItems={() => {}}
        portalOptions={{ offsetX: 4 }}
      />
    </div>
  );
}

SelectLayer.propTypes = {
  id: PropTypes.string,
  value: PropTypes.object,
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  type: PropTypes.oneOf(['default', 'modal']),
  label: PropTypes.string,
  error: PropTypes.bool,
  isDisabled: PropTypes.bool,
  helperText: PropTypes.string,
  placeholder: PropTypes.string,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
};

export default SelectLayer;
